'use client'

import Link from 'next/link'
import { type ComponentProps, forwardRef } from 'react'
import {
  trackPremiumCtaClicado,
  trackHomeCardClicado,
  trackGlossarioTermoClicado,
  trackAtividadeMeiClicada,
  trackLinkInternoClicado,
  type PremiumCtaLocal,
} from '@/lib/analytics'

type LinkBaseProps = ComponentProps<typeof Link>

/**
 * Wrapper helper: chama o tracker, depois chama o onClick passado pelo
 * caller (se existir). Permite combinar tracking com outros handlers.
 */
function chainOnClick<T extends React.MouseEvent<HTMLAnchorElement>>(
  track: () => void,
  userOnClick?: (e: T) => void,
) {
  return (e: T) => {
    track()
    userOnClick?.(e)
  }
}

type PremiumLinkProps = LinkBaseProps & { local: PremiumCtaLocal }

/**
 * <Link> que dispara `premium_cta_clicado` no clique. Aceita `local`
 * como string serializavel — funciona dentro de server components e
 * dentro de <Button asChild>.
 */
export const PremiumLink = forwardRef<HTMLAnchorElement, PremiumLinkProps>(
  function PremiumLink({ local, onClick, ...rest }, ref) {
    return (
      <Link
        ref={ref}
        {...rest}
        onClick={chainOnClick(() => trackPremiumCtaClicado(local), onClick)}
      />
    )
  }
)

type HomeCardLinkProps = LinkBaseProps & { calculadora: string }

export const HomeCardLink = forwardRef<HTMLAnchorElement, HomeCardLinkProps>(
  function HomeCardLink({ calculadora, onClick, ...rest }, ref) {
    return (
      <Link
        ref={ref}
        {...rest}
        onClick={chainOnClick(() => trackHomeCardClicado(calculadora), onClick)}
      />
    )
  }
)

type GlossarioTermLinkProps = LinkBaseProps & { termo: string }

export const GlossarioTermLink = forwardRef<HTMLAnchorElement, GlossarioTermLinkProps>(
  function GlossarioTermLink({ termo, onClick, ...rest }, ref) {
    return (
      <Link
        ref={ref}
        {...rest}
        onClick={chainOnClick(() => trackGlossarioTermoClicado(termo), onClick)}
      />
    )
  }
)

type AtividadeMeiLinkProps = LinkBaseProps & { atividade: string }

export const AtividadeMeiLink = forwardRef<HTMLAnchorElement, AtividadeMeiLinkProps>(
  function AtividadeMeiLink({ atividade, onClick, ...rest }, ref) {
    return (
      <Link
        ref={ref}
        {...rest}
        onClick={chainOnClick(() => trackAtividadeMeiClicada(atividade), onClick)}
      />
    )
  }
)

type NavLinkProps = LinkBaseProps & {
  secao: 'header' | 'footer'
  rotulo: string
}

/**
 * <Link> de navegacao (header/footer) que dispara link_interno_clicado.
 */
export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(
  function NavLink({ secao, rotulo, onClick, ...rest }, ref) {
    return (
      <Link
        ref={ref}
        {...rest}
        onClick={chainOnClick(() => trackLinkInternoClicado(secao, rotulo), onClick)}
      />
    )
  }
)
